import { isArrayEmpty } from "./utils/array";


interface Promise<T> {
    /**
    * Attaches a callback that is invoked when the Promise is settled (fulfilled or rejected). The
    * resolved value cannot be modified from the callback.
    * @param onfinally The callback to execute when the Promise is settled (fulfilled or rejected).
    * @returns A Promise for the completion of the callback.
    */
    finally(onfinally?: (() => void) | undefined | null): Promise<T>
}

export interface OmniaPromise<T> {
    /**
     * Attaches callbacks for the resolution and/or rejection of the Promise.
     * @param onfulfilled The callback to execute when the Promise is resolved.
     * @param onrejected The callback to execute when the Promise is rejected.
     * @returns A Promise for the completion of which ever callback is executed.
     */
    then<TResult1 = T, TResult2 = never>(onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | undefined | null, onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null): OmniaPromise<TResult1 | TResult2>;

    /**
     * Attaches a callback for only the rejection of the Promise.
     * @param onrejected The callback to execute when the Promise is rejected.
     * @returns A Promise for the completion of the callback.
     */
    catch<TResult = never>(onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null): OmniaPromise<T | TResult>;
    /**
    * Attaches a callback that is invoked when the Promise is settled (fulfilled or rejected). The
    * resolved value cannot be modified from the callback.
    * @param onfinally The callback to execute when the Promise is settled (fulfilled or rejected).
    * @returns A Promise for the completion of the callback.
    */
    finally(onfinally?: (() => void) | undefined | null): OmniaPromise<T>
}

export interface OmniaDefer<T> {
    readonly promise: OmniaPromise<T>
    resolve(result?: T): void
}


let _defer: () => OmniaDefer<any>
let _all: (promises: PromiseLike<any>[]) => OmniaPromise<any>

/**
 * This is called once when Omnia is initialized, to as to be able to set local variables _defer and _all,
 * which are used in various promise-related utilities
 * @param $q Angular's $q service
 *
 * @remarks
 * As long as we depend on AngularJs, all promises should be created/handled by the $q service to make sure
 * that Angular is notified when a promise (specially an HTTP promise) is resolved of rejected
 * It is however a bit impractical to inject $q everywhere, so that it is injected in global variables at this stage,
 * and referenced later
 *
 */
export function setupPromises($q: ng.IQService) {

    if (_defer) {
        throw new Error("Already initialized")
    }

    _defer = $q.defer
    _all = $q.all

}

/**
 * Creates a deferred result
 * @returns
 */
export function omniaDefer<T>() {

    if (!_defer) {
        throw new Error("Not implemented")
    }

    return _defer() as OmniaDefer<T>

}

/**
 * Creates a resolved promise
 * @param result Result
 * @returns Resolved promise
 */
export function omniaResolve<T>(result?: T) {

    const d = omniaDefer<T>()

    d.resolve(result)

    return d.promise

}

/**
 * Returns a new promse
 * @param run Function to execute
 * @returns Promise
 */
export function omniaPromise<T>(run: (resolve: (result: T) => void) => void): OmniaPromise<T> {

    const d = omniaDefer<T>()

    run(d.resolve)

    return d.promise

}

/**
 * Waits for multiple promises
 * @param promises Promises to wait
 * @returns Single promise resolved when all promises are resolved
 */
export function omniaAllPromises<T = any>(promises: PromiseLike<any>[]): OmniaPromise<T[]> {

    return _all(promises)

}

/**
 * Waits for multiple promises executed one after the other
 * @param sources Generic array of items, each providing a promise
 * @param taskProvider Task provider - i.e., function returning a promise for each item it the array
 * @returns Promse resolved when all promises have been resolved, one after the other
 */
export function omniaAllSeries<T, R>(sources: T[], taskProvider: (source: T) => OmniaPromise<R>): OmniaPromise<R[]> {

    if (isArrayEmpty(sources)) {
        return omniaResolve([])
    }

    function run(index: number): OmniaPromise<R[]> {

        const p = taskProvider(sources[index])

        if (index < (sources.length - 1)) {
            return p.then(r => run(index + 1).then(a => [r].concat(a)))
        }
        else {
            return p.then(r => [r])
        }

    }

    return run(0)

}


/**
 * Implements a timeout as a promise
 * @param timeout Timeout in ms
 * @returns Promise-like resolved at the end of the timeout
 */
export function wait(timeout: number): PromiseLike<void> {

    const defer = omniaDefer<void>()

    window.setTimeout(() => defer.resolve(), timeout)

    return defer.promise

}
